import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Home, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * 使用条款页面
 */
function TermsOfService() {
  const { language } = useLanguage();

  const content = {
    zh: {
      title: '使用条款',
      lastUpdated: '最后更新：2025年12月23日',
      intro: '欢迎使用 DevToolkit！通过访问和使用我们的服务，您同意遵守以下使用条款。请仔细阅读这些条款。',
      sections: [
        {
          title: '1. 服务说明',
          content: `
            <p>DevToolkit 提供以下免费在线开发工具：</p>
            <ul>
              <li><strong>JSON 格式化工具：</strong>JSON 格式化、美化、验证、表格视图展示、错误修复等</li>
              <li><strong>Go Struct 转换工具：</strong>JSON 与 Go 结构体的双向转换</li>
              <li><strong>时间戳转换工具：</strong>Unix 时间戳与日期时间的互相转换</li>
              <li><strong>博客和文档：</strong>开发者相关的技术文章和教程</li>
            </ul>
            <p>我们的服务完全免费，无需注册即可使用。</p>
          `
        },
        {
          title: '2. 使用许可',
          content: `
            <h3>2.1 授予的权利</h3>
            <p>我们授予您有限的、非独占的、不可转让的、可撤销的权利来访问和使用 DevToolkit 的服务。</p>

            <h3>2.2 使用限制</h3>
            <p>在使用我们的服务时，您<strong>不得</strong>：</p>
            <ul>
              <li>❌ 以任何非法目的使用服务</li>
              <li>❌ 试图获取对系统或网络的未授权访问</li>
              <li>❌ 干扰或破坏服务的正常运行</li>
              <li>❌ 使用自动化工具（爬虫、机器人等）过度访问服务</li>
              <li>❌ 复制、修改、分发、出售或出租服务的任何部分</li>
              <li>❌ 对服务进行逆向工程、反编译或反汇编</li>
              <li>❌ 上传或传播恶意软件、病毒或其他有害代码</li>
              <li>❌ 侵犯他人的知识产权或隐私权</li>
            </ul>
          `
        },
        {
          title: '3. 用户内容',
          content: `
            <h3>3.1 您的数据</h3>
            <p>您在工具中输入的所有数据（JSON、时间戳等）：</p>
            <ul>
              <li>✅ 完全在您的浏览器本地处理</li>
              <li>✅ 不会上传到我们的服务器</li>
              <li>✅ 您保留对数据的所有权利</li>
            </ul>

            <h3>3.2 本地存储</h3>
            <p>我们使用浏览器的本地存储来保存：</p>
            <ul>
              <li>用户偏好设置（主题、语言）</li>
              <li>JSON 输入历史记录</li>
            </ul>
            <p>这些数据仅存储在您的设备上，您可以随时清除。</p>

            <h3>3.3 反馈和建议</h3>
            <p>如果您向我们提供反馈、建议或其他信息：</p>
            <ul>
              <li>您授予我们免费使用这些信息的权利</li>
              <li>我们可以将这些信息用于改进服务</li>
              <li>您不会因此获得任何补偿</li>
            </ul>
          `
        },
        {
          title: '4. 知识产权',
          content: `
            <h3>4.1 我们的知识产权</h3>
            <p>DevToolkit 的所有内容、功能和特性，包括但不限于：</p>
            <ul>
              <li>网站设计、布局和外观</li>
              <li>文本、图形、图片、徽标</li>
              <li>软件代码和算法</li>
              <li>博客文章和文档</li>
            </ul>
            <p>均归我们或我们的许可方所有，受版权、商标和其他知识产权法律保护。</p>

            <h3>4.2 开源代码</h3>
            <p>DevToolkit 是开源项目，源代码在 <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a> 上发布。</p>
            <ul>
              <li>您可以查看、复制和使用源代码</li>
              <li>使用时需遵守 MIT 许可证的条款</li>
              <li>欢迎贡献代码和提出改进建议</li>
            </ul>

            <h3>4.3 商标</h3>
            <p>"DevToolkit" 名称和徽标是我们的商标。未经书面许可，您不得使用这些商标。</p>
          `
        },
        {
          title: '5. 第三方服务',
          content: `
            <p>我们的服务集成了以下第三方服务：</p>
            <ul>
              <li><strong>Google Analytics：</strong>网站分析服务</li>
              <li><strong>Google AdSense：</strong>广告服务</li>
              <li><strong>Cloudflare：</strong>CDN 和安全服务</li>
            </ul>
            <p>这些第三方服务有各自的使用条款和隐私政策：</p>
            <ul>
              <li><a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Google 服务条款</a></li>
              <li><a href="https://www.cloudflare.com/terms/" target="_blank" rel="noopener noreferrer">Cloudflare 条款</a></li>
            </ul>
            <p>我们不对第三方服务的内容、隐私政策或做法负责。</p>
          `
        },
        {
          title: '6. 免责声明',
          content: `
            <h3>6.1 "按现状"提供</h3>
            <p>DevToolkit 的服务按"按现状"和"按可用性"提供，不提供任何明示或暗示的保证，包括但不限于：</p>
            <ul>
              <li>适销性保证</li>
              <li>特定用途适用性保证</li>
              <li>不侵权保证</li>
            </ul>

            <h3>6.2 准确性</h3>
            <p>虽然我们努力确保工具的准确性和可靠性，但我们不保证：</p>
            <ul>
              <li>服务将不间断或无错误</li>
              <li>结果将准确或可靠</li>
              <li>错误将被纠正</li>
            </ul>

            <h3>6.3 使用风险</h3>
            <p>您使用 DevToolkit 的服务需自行承担风险。建议您：</p>
            <ul>
              <li>在生产环境使用前验证工具输出</li>
              <li>备份重要数据</li>
              <li>不要依赖服务进行关键任务</li>
            </ul>
          `
        },
        {
          title: '7. 责任限制',
          content: `
            <p>在法律允许的最大范围内，DevToolkit 及其运营者、员工或贡献者对以下情况不承担责任：</p>
            <ul>
              <li>任何间接、偶然、特殊、惩罚性或后果性损害</li>
              <li>利润、收入、数据或使用机会的损失</li>
              <li>业务中断造成的损失</li>
              <li>因使用或无法使用服务而产生的任何损害</li>
            </ul>
            <p>即使我们已被告知可能发生此类损害。</p>
            <p><strong>某些司法管辖区不允许排除或限制责任，因此上述限制可能不适用于您。</strong></p>
          `
        },
        {
          title: '8. 赔偿',
          content: `
            <p>您同意赔偿、辩护并使 DevToolkit 及其运营者、员工、贡献者免受因以下原因引起的任何索赔、损失、责任、费用（包括合理的律师费）：</p>
            <ul>
              <li>您对服务的使用或滥用</li>
              <li>您违反这些使用条款</li>
              <li>您侵犯他人权利</li>
              <li>您提供的内容或信息</li>
            </ul>
          `
        },
        {
          title: '9. 服务变更和终止',
          content: `
            <h3>9.1 服务变更</h3>
            <p>我们保留随时修改或停止服务（或其任何部分）的权利，恕不另行通知。</p>

            <h3>9.2 终止使用</h3>
            <p>我们可能因以下原因终止或暂停您对服务的访问：</p>
            <ul>
              <li>违反这些使用条款</li>
              <li>滥用服务或过度使用</li>
              <li>从事非法活动</li>
              <li>应法律要求</li>
            </ul>
            <p>终止后，您继续使用服务的权利将立即失效。</p>
          `
        },
        {
          title: '10. 争议解决',
          content: `
            <h3>10.1 适用法律</h3>
            <p>这些使用条款受中华人民共和国法律管辖，不考虑其法律冲突原则。</p>

            <h3>10.2 争议解决方式</h3>
            <p>因使用 DevToolkit 引起的任何争议或索赔应首先通过友好协商解决。如果协商失败，争议应提交有管辖权的法院解决。</p>
          `
        },
        {
          title: '11. 其他条款',
          content: `
            <h3>11.1 完整协议</h3>
            <p>这些使用条款构成您与 DevToolkit 之间关于服务使用的完整协议，取代所有先前的协议和理解。</p>

            <h3>11.2 可分割性</h3>
            <p>如果这些条款的任何规定被认定为无效或不可执行，其余规定将继续有效。</p>

            <h3>11.3 不弃权</h3>
            <p>我们未能执行这些条款的任何权利或规定不构成对该权利或规定的放弃。</p>

            <h3>11.4 条款变更</h3>
            <p>我们可能会不时更新这些使用条款。重大变更时，我们会：</p>
            <ul>
              <li>更新本页面顶部的"最后更新"日期</li>
              <li>在网站上发布通知</li>
            </ul>
            <p>您在条款变更后继续使用服务即表示接受修订后的条款。</p>
          `
        },
        {
          title: '12. 联系我们',
          content: `
            <p>如果您对这些使用条款有任何疑问或建议，请通过以下方式联系我们：</p>
            <ul>
              <li><strong>GitHub：</strong><a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">提交 Issue</a></li>
              <li><strong>网站：</strong><a href="/contact">联系页面</a></li>
            </ul>
            <p>感谢您使用 DevToolkit！</p>
          `
        }
      ]
    },
    en: {
      title: 'Terms of Service',
      lastUpdated: 'Last Updated: December 23, 2025',
      intro: 'Welcome to DevToolkit! By accessing and using our services, you agree to comply with the following terms of service. Please read these terms carefully.',
      sections: [
        {
          title: '1. Service Description',
          content: `
            <p>DevToolkit provides the following free online development tools:</p>
            <ul>
              <li><strong>JSON Formatter:</strong> JSON formatting, beautification, validation, table view display, error fixing, etc.</li>
              <li><strong>Go Struct Converter:</strong> Bidirectional conversion between JSON and Go structs</li>
              <li><strong>Timestamp Converter:</strong> Conversion between Unix timestamps and datetime</li>
              <li><strong>Blog and Documentation:</strong> Technical articles and tutorials for developers</li>
            </ul>
            <p>Our services are completely free and require no registration to use.</p>
          `
        },
        {
          title: '2. License to Use',
          content: `
            <h3>2.1 Granted Rights</h3>
            <p>We grant you a limited, non-exclusive, non-transferable, revocable right to access and use DevToolkit's services.</p>

            <h3>2.2 Usage Restrictions</h3>
            <p>When using our services, you <strong>must NOT</strong>:</p>
            <ul>
              <li>❌ Use services for any illegal purpose</li>
              <li>❌ Attempt to gain unauthorized access to systems or networks</li>
              <li>❌ Interfere with or disrupt the normal operation of services</li>
              <li>❌ Use automated tools (scrapers, bots, etc.) to excessively access services</li>
              <li>❌ Copy, modify, distribute, sell, or rent any part of the services</li>
              <li>❌ Reverse engineer, decompile, or disassemble services</li>
              <li>❌ Upload or distribute malware, viruses, or other harmful code</li>
              <li>❌ Infringe on others' intellectual property or privacy rights</li>
            </ul>
          `
        },
        {
          title: '3. User Content',
          content: `
            <h3>3.1 Your Data</h3>
            <p>All data you input into tools (JSON, timestamps, etc.):</p>
            <ul>
              <li>✅ Is processed entirely locally in your browser</li>
              <li>✅ Is not uploaded to our servers</li>
              <li>✅ You retain all rights to your data</li>
            </ul>

            <h3>3.2 Local Storage</h3>
            <p>We use browser local storage to save:</p>
            <ul>
              <li>User preferences (theme, language)</li>
              <li>JSON input history</li>
            </ul>
            <p>This data is stored only on your device and you can clear it at any time.</p>

            <h3>3.3 Feedback and Suggestions</h3>
            <p>If you provide us with feedback, suggestions, or other information:</p>
            <ul>
              <li>You grant us the right to use this information free of charge</li>
              <li>We may use this information to improve services</li>
              <li>You will not receive any compensation</li>
            </ul>
          `
        },
        {
          title: '4. Intellectual Property',
          content: `
            <h3>4.1 Our Intellectual Property</h3>
            <p>All content, features, and functionality of DevToolkit, including but not limited to:</p>
            <ul>
              <li>Website design, layout, and appearance</li>
              <li>Text, graphics, images, logos</li>
              <li>Software code and algorithms</li>
              <li>Blog posts and documentation</li>
            </ul>
            <p>Are owned by us or our licensors and protected by copyright, trademark, and other intellectual property laws.</p>

            <h3>4.2 Open Source Code</h3>
            <p>DevToolkit is an open-source project with source code published on <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a>.</p>
            <ul>
              <li>You may view, copy, and use the source code</li>
              <li>Usage must comply with MIT License terms</li>
              <li>Contributions and improvement suggestions are welcome</li>
            </ul>

            <h3>4.3 Trademarks</h3>
            <p>The "DevToolkit" name and logo are our trademarks. You may not use these trademarks without written permission.</p>
          `
        },
        {
          title: '5. Third-Party Services',
          content: `
            <p>Our services integrate the following third-party services:</p>
            <ul>
              <li><strong>Google Analytics:</strong> Website analytics service</li>
              <li><strong>Google AdSense:</strong> Advertising service</li>
              <li><strong>Cloudflare:</strong> CDN and security service</li>
            </ul>
            <p>These third-party services have their own terms of service and privacy policies:</p>
            <ul>
              <li><a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Google Terms of Service</a></li>
              <li><a href="https://www.cloudflare.com/terms/" target="_blank" rel="noopener noreferrer">Cloudflare Terms</a></li>
            </ul>
            <p>We are not responsible for the content, privacy policies, or practices of third-party services.</p>
          `
        },
        {
          title: '6. Disclaimer',
          content: `
            <h3>6.1 "As Is" Provision</h3>
            <p>DevToolkit's services are provided "as is" and "as available" without any express or implied warranties, including but not limited to:</p>
            <ul>
              <li>Warranties of merchantability</li>
              <li>Warranties of fitness for a particular purpose</li>
              <li>Warranties of non-infringement</li>
            </ul>

            <h3>6.2 Accuracy</h3>
            <p>While we strive to ensure accuracy and reliability of tools, we do not warrant that:</p>
            <ul>
              <li>Services will be uninterrupted or error-free</li>
              <li>Results will be accurate or reliable</li>
              <li>Errors will be corrected</li>
            </ul>

            <h3>6.3 Usage Risk</h3>
            <p>You use DevToolkit's services at your own risk. We recommend that you:</p>
            <ul>
              <li>Verify tool output before production use</li>
              <li>Back up important data</li>
              <li>Do not rely on services for mission-critical tasks</li>
            </ul>
          `
        },
        {
          title: '7. Limitation of Liability',
          content: `
            <p>To the maximum extent permitted by law, DevToolkit and its operators, employees, or contributors shall not be liable for:</p>
            <ul>
              <li>Any indirect, incidental, special, punitive, or consequential damages</li>
              <li>Loss of profits, revenue, data, or opportunity to use</li>
              <li>Loss caused by business interruption</li>
              <li>Any damages arising from use or inability to use services</li>
            </ul>
            <p>Even if we have been advised of the possibility of such damages.</p>
            <p><strong>Some jurisdictions do not allow exclusion or limitation of liability, so the above limitations may not apply to you.</strong></p>
          `
        },
        {
          title: '8. Indemnification',
          content: `
            <p>You agree to indemnify, defend, and hold harmless DevToolkit and its operators, employees, contributors from any claims, losses, liabilities, expenses (including reasonable attorney fees) arising from:</p>
            <ul>
              <li>Your use or misuse of services</li>
              <li>Your violation of these terms of service</li>
              <li>Your infringement of others' rights</li>
              <li>Content or information you provide</li>
            </ul>
          `
        },
        {
          title: '9. Service Changes and Termination',
          content: `
            <h3>9.1 Service Changes</h3>
            <p>We reserve the right to modify or discontinue services (or any part thereof) at any time without notice.</p>

            <h3>9.2 Termination of Use</h3>
            <p>We may terminate or suspend your access to services for the following reasons:</p>
            <ul>
              <li>Violation of these terms of service</li>
              <li>Abuse or excessive use of services</li>
              <li>Engaging in illegal activities</li>
              <li>As required by law</li>
            </ul>
            <p>Upon termination, your right to continue using services will cease immediately.</p>
          `
        },
        {
          title: '10. Dispute Resolution',
          content: `
            <h3>10.1 Governing Law</h3>
            <p>These terms of service are governed by the laws of the People's Republic of China, without regard to its conflict of law principles.</p>

            <h3>10.2 Dispute Resolution Method</h3>
            <p>Any disputes or claims arising from the use of DevToolkit should first be resolved through friendly negotiation. If negotiation fails, disputes should be submitted to courts with jurisdiction.</p>
          `
        },
        {
          title: '11. Other Terms',
          content: `
            <h3>11.1 Entire Agreement</h3>
            <p>These terms of service constitute the entire agreement between you and DevToolkit regarding service use, superseding all prior agreements and understandings.</p>

            <h3>11.2 Severability</h3>
            <p>If any provision of these terms is found invalid or unenforceable, the remaining provisions will continue in effect.</p>

            <h3>11.3 No Waiver</h3>
            <p>Our failure to enforce any right or provision of these terms does not constitute a waiver of that right or provision.</p>

            <h3>11.4 Changes to Terms</h3>
            <p>We may update these terms of service from time to time. For major changes, we will:</p>
            <ul>
              <li>Update the "Last Updated" date at the top of this page</li>
              <li>Post a notice on the website</li>
            </ul>
            <p>Your continued use of services after changes constitutes acceptance of the revised terms.</p>
          `
        },
        {
          title: '12. Contact Us',
          content: `
            <p>If you have any questions or suggestions about these terms of service, please contact us through:</p>
            <ul>
              <li><strong>GitHub:</strong> <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">Submit an Issue</a></li>
              <li><strong>Website:</strong> <a href="/contact">Contact Page</a></li>
            </ul>
            <p>Thank you for using DevToolkit!</p>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="min-h-screen bg-[#faf9f6] dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 dark:from-blue-600 dark:to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <FileText size={32} />
            <h1 className="text-4xl font-bold">{data.title}</h1>
          </div>
          <p className="text-lg text-blue-100">{data.lastUpdated}</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1">
              <Home size={14} />
              {language === 'zh' ? '首页' : 'Home'}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{data.title}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Intro */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-12">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{data.intro}</p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-1
        ">
          {data.sections.map((section, index) => (
            <section key={index}>
              <h2>{section.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </section>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </article>
    </div>
  );
}

export default TermsOfService;
